import React from 'react';
import { CalendarDays, Check, ChevronRight, Clock, Info, Minus, UtensilsCrossed } from 'lucide-react';
import { num0 } from '../../lib/numeros';
import ExtrasDelDia from '../nutrition/ExtrasDelDia';

/*
 * LA COLUMNA DE NUTRICIÓN DEL INICIO, REDISEÑADA (15-10). Separa dos preguntas que antes se
 * mezclaban en la misma tarjeta:
 *   - «Dieta planificada»: ¿está el día montado y cuadrado? (estado de cada comida)
 *   - «Comidas de hoy»: ¿qué me he comido ya? (la marca de cada toma)
 * Lo consumido frente al objetivo va arriba, en ProgresoHoy.
 *
 * NINGUNA REGLA NUEVA. El estado de cada comida es `t.estado` de useDiaDeHoy
 * ('vacia' | 'cuadrada' | 'falta' | 'sobra', la misma vara que Nutrición) y la marca es
 * `marcar(clave)`, igual que antes: aquí solo se pinta.
 */

const VERDE = 'text-emerald-400';

// Mientras no ha llegado el reparto, una comida con alimentos tiene estado null: no se sabe
// si cuadra. Se pinta como «con alimentos» sin decir si está lista.
const listaDe = (t) => t.estado === 'cuadrada';
const conAlimentos = (t) => t.tieneAlimentos;

// ---------------------------------------------------------------- Dieta planificada

const Paso = ({ t, indice }) => {
    const lista = listaDe(t);
    const aviso = t.estado === 'falta' || t.estado === 'sobra';
    return (
        <div className="min-w-0 flex flex-col items-center text-center" data-testid={`plan-${t.clave}`}>
            <span className="flex items-center gap-1.5">
                <span className={`w-7 h-7 rounded-full flex items-center justify-center border-2
                    ${lista ? 'bg-emerald-500 border-emerald-500 text-black'
                        : aviso ? 'bg-amber-500 border-amber-500 text-black'
                            : conAlimentos(t) ? 'border-foreground/50 text-foreground/70'
                                : 'border-white/25 text-transparent'}`}>
                    {lista ? <Check className="w-4 h-4" strokeWidth={3} />
                        : aviso ? <span className="text-sm font-black leading-none">!</span>
                            : conAlimentos(t) ? <span className="w-2 h-2 rounded-full bg-current" /> : null}
                </span>
                <span className="text-sm font-bold text-foreground">{indice}</span>
            </span>
            <span className="text-xs text-muted-foreground mt-1.5 leading-tight break-words max-w-full">{t.nombre}</span>
        </div>
    );
};

const resumenDelPlan = (comidas, repartoResuelto) => {
    const conAlgo = comidas.filter(conAlimentos);
    const vacias = comidas.filter((t) => !conAlimentos(t));
    if (!conAlgo.length) {
        return { tono: 'neutro', titulo: 'Tu día está sin planificar', accion: 'Planificar', destino: comidas[0] };
    }
    if (vacias.length) {
        return {
            tono: 'brand', titulo: 'Menú incompleto',
            detalle: `${vacias.length === 1 ? '1 comida' : `${vacias.length} comidas`} por planificar`,
            accion: 'Completar menú', destino: vacias[0],
        };
    }
    if (!repartoResuelto) return null;
    const fuera = comidas.filter((t) => t.estado === 'falta' || t.estado === 'sobra');
    if (fuera.length) {
        return {
            tono: 'ambar', titulo: 'Ajustar cantidades', detalle: fuera.map((t) => t.nombre).join(', '),
            accion: 'Ajustar', destino: fuera[0],
        };
    }
    if (comidas.every(listaDe)) return { tono: 'verde', titulo: 'Menú listo', detalle: 'Todo cuadra con tus macros' };
    return null;
};

const TONO_RESUMEN = {
    neutro: 'text-foreground', brand: 'text-foreground', ambar: 'text-amber-200', verde: VERDE,
};

/** A dónde lleva una comida: Nutrición abierta en ella (o sin comida, a Nutrición). */
export const rutaDeLaComida = (t) => (t ? `/dashboard/nutrition?comida=${t.clave}` : '/dashboard/nutrition');

/**
 * «Dieta planificada»: la de Inicio y la del check-in diario (Francisco, 28-10). `tomas` son
 * las de `useDiaDeHoy`; el intra y el post los pone la app y no se «planifican».
 */
export const DietaPlanificada = ({ tomas, repartoResuelto, abrir }) => {
    const comidas = tomas.filter((t) => !t.esPeri);
    if (!comidas.length) return null;
    const listas = comidas.filter(listaDe).length;
    const resumen = resumenDelPlan(comidas, repartoResuelto);
    return (
        <section className="rounded-2xl border border-border bg-card p-4 sm:p-5" data-testid="dieta-planificada">
            <header className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                <h2 className="flex items-center gap-2.5 font-heading text-lg font-bold uppercase tracking-wide text-foreground">
                    <CalendarDays className="w-5 h-5 text-brand" strokeWidth={2.25} /> Dieta planificada
                </h2>
                {repartoResuelto && (
                    <span className="text-xs text-muted-foreground flex-shrink-0">
                        {listas} de {comidas.length} {comidas.length === 1 ? 'comida lista' : 'comidas listas'}
                    </span>
                )}
            </header>

            {/* Los pasos, unidos por una línea que va por detrás de los círculos. */}
            <div className="relative mt-4">
                {comidas.length > 1 && (
                    <span aria-hidden="true" className="absolute top-3.5 h-px bg-white/15"
                        style={{ left: `${50 / comidas.length}%`, right: `${50 / comidas.length}%` }} />
                )}
                <ol className="relative flex">
                    {comidas.map((t, i) => (
                        <li key={t.clave} className="flex-1 min-w-0 flex justify-center">
                            <span className="bg-card px-1.5 min-w-0 max-w-full"><Paso t={t} indice={i + 1} /></span>
                        </li>
                    ))}
                </ol>
            </div>

            {/* El estado arriba y lo que falta debajo; la acción baja a otra fila si no cabe. */}
            {resumen && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mt-4 pt-3 border-t border-white/5">
                    <div className="flex items-start gap-2 min-w-0 flex-1 basis-48">
                        {resumen.tono === 'verde'
                            ? <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${VERDE}`} strokeWidth={3} />
                            : <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-muted-foreground" />}
                        <p className="min-w-0 text-sm leading-snug" data-testid="aviso-planificar">
                            <span className={`block font-semibold ${TONO_RESUMEN[resumen.tono]}`}>{resumen.titulo}</span>
                            {resumen.detalle && <span className="block text-muted-foreground break-words">{resumen.detalle}</span>}
                        </p>
                    </div>
                    {resumen.accion && (
                        <button type="button" onClick={() => abrir(resumen.destino)}
                            className="ml-auto flex items-center min-h-[40px] px-1 text-sm font-bold text-brand hover:brightness-110 flex-shrink-0">
                            {resumen.accion} <ChevronRight className="w-4 h-4" />
                        </button>
                    )}
                </div>
            )}
        </section>
    );
};

// ---------------------------------------------------------------- Comidas de hoy

const lineaMacros = (m, sinGrasa) => [
    `+${num0(m?.P || 0)} P`, `+${num0(m?.H || 0)} H`, ...(sinGrasa ? [] : [`+${num0(m?.G || 0)} G`]),
].join(' · ');

// Tres estados, con su color: consumida (verde), planificada sin comer (naranja), vacía (gris).
const estadoDeLaComida = (t) => (t.marcada ? 'consumida' : t.tieneAlimentos ? 'pendiente' : 'vacia');

const ESTILO = {
    consumida: { borde: 'border-l-emerald-500', aro: 'border-emerald-500 text-emerald-400 bg-emerald-500/10' },
    pendiente: { borde: 'border-l-brand', aro: 'border-brand text-brand bg-brand/10' },
    vacia: { borde: 'border-l-white/15', aro: 'border-white/25 text-white/40' },
};

const TarjetaComida = ({ toma: t, onAbrir, onMarcar }) => {
    const estado = estadoDeLaComida(t);
    const estilo = ESTILO[estado];
    // Una comida vacía no se puede marcar (no hay nada que haberse comido); si venía
    // marcada de antes, se deja desmarcar.
    const sePuedeMarcar = t.tieneAlimentos || t.marcada;
    const alimentos = t.alimentosConCantidad?.length ? t.alimentosConCantidad : t.alimentos;
    return (
        <div className={`rounded-2xl border border-border border-l-4 ${estilo.borde} bg-card p-3 sm:p-4`}
            data-testid={`comida-hoy-${t.clave}`}>
            {/* 1 · el check, el nombre (puede ocupar dos líneas) y la flecha al detalle. */}
            <div className="flex items-start gap-3">
                <button type="button" onClick={onMarcar} role="checkbox" aria-checked={t.marcada}
                    disabled={!sePuedeMarcar}
                    aria-label={`${t.nombre}: ${t.marcada ? 'consumida, pulsa para desmarcar'
                        : sePuedeMarcar ? 'marcar como consumida' : 'añade algún alimento para marcarla'}`}
                    data-testid={`marcar-${t.clave}`}
                    className={`w-11 h-11 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition
                        ${estilo.aro} ${sePuedeMarcar ? 'hover:brightness-125' : 'cursor-default'}`}>
                    {estado === 'consumida' ? <Check className="w-5 h-5" strokeWidth={3} />
                        : estado === 'pendiente' ? <Clock className="w-5 h-5" strokeWidth={2.25} />
                            : <Minus className="w-5 h-5" strokeWidth={2.5} />}
                </button>

                {/* 2 · debajo del nombre, los alimentos (hasta dos líneas: la lista entera
                    está en el detalle, a un toque) y los macros. */}
                <button type="button" onClick={onAbrir} className="min-w-0 flex-1 text-left pt-0.5"
                    title={alimentos.length ? alimentos.join(', ') : undefined}>
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="font-heading text-lg font-bold text-foreground leading-tight break-words">{t.nombre}</span>
                        {estado === 'consumida' && (
                            <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-400 bg-emerald-500/15 rounded-full px-2 py-0.5">
                                <Check className="w-3.5 h-3.5" strokeWidth={3} /> Consumida
                            </span>
                        )}
                    </span>
                    <span className="text-sm text-muted-foreground leading-snug mt-1 line-clamp-2 break-words">
                        {alimentos.length ? alimentos.join(', ') : 'Sin planificar'}
                    </span>
                    {t.tieneAlimentos && (
                        <span className="block text-xs text-foreground/60 font-data mt-1 whitespace-nowrap">{lineaMacros(t.montado, t.esPeri)}</span>
                    )}
                </button>

                <button type="button" onClick={onAbrir} aria-label={`Ver ${t.nombre}`}
                    className="-mr-1 w-8 h-11 flex items-center justify-center text-muted-foreground hover:text-foreground flex-shrink-0">
                    <ChevronRight className="w-5 h-5" />
                </button>
            </div>

            {/* 3 · la acción, en su fila: a lo ancho en el teléfono, alineada con el texto
                desde sm. La consumida no lleva botón: se desmarca en el check. */}
            {estado !== 'consumida' && (
                <div className="mt-3 sm:pl-14">
                    {estado === 'pendiente' ? (
                        <button type="button" onClick={onMarcar} data-testid={`consumir-${t.clave}`}
                            className="w-full sm:w-auto min-h-[44px] px-5 rounded-full bg-brand text-white text-sm font-bold shadow-lg shadow-brand/25 hover:brightness-110">
                            Marcar consumida
                        </button>
                    ) : (
                        <button type="button" onClick={onAbrir} data-testid={`anadir-${t.clave}`}
                            className="w-full sm:w-auto min-h-[44px] px-5 rounded-full bg-white/10 text-foreground/90 text-sm font-semibold hover:bg-white/15 inline-flex items-center justify-center gap-2">
                            <CalendarDays className="w-4 h-4" /> Planificar
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

const subirALosMacros = () => {
    const destino = () => document.querySelector('[data-testid="tu-dieta-hoy"]');
    destino()?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setTimeout(() => {
        const el = destino();
        if (el && Math.abs(el.getBoundingClientRect().top) > 150) el.scrollIntoView({ block: 'start' });
    }, 900);
};

const NutricionHoy = ({ api, fecha, tomas, marcar, extras, setExtras, navigate, repartoResuelto }) => {
    const abrir = (t) => navigate(rutaDeLaComida(t));
    const consumidas = tomas.filter((t) => t.marcada).length;
    return (
        <div className="space-y-6" data-testid="nutricion-hoy">
            <DietaPlanificada tomas={tomas} repartoResuelto={repartoResuelto} abrir={abrir} />

            <section className="space-y-3">
                <header className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                    <h2 className="flex items-center gap-2.5 font-heading text-xl font-bold uppercase tracking-wide text-foreground">
                        <UtensilsCrossed className="w-5 h-5 text-brand" strokeWidth={2.25} /> Comidas de hoy
                    </h2>
                    <span className="text-xs text-muted-foreground flex-shrink-0">
                        {consumidas} de {tomas.length} {tomas.length === 1 ? 'completada' : 'completadas'}
                    </span>
                </header>
                {tomas.map((t) => (
                    <TarjetaComida key={t.clave} toma={t}
                        onAbrir={() => abrir(t)}
                        onMarcar={() => {
                            marcar(t.clave);
                            if (!t.marcada) subirALosMacros();
                        }} />
                ))}
                <ExtrasDelDia api={api} fecha={fecha} extras={extras} origen="inicio" plegable compacto
                    onAnadido={(e) => setExtras((prev) => [...prev, e])}
                    onQuitado={(id) => setExtras((prev) => prev.filter((x) => x.id !== id))} />
            </section>
        </div>
    );
};

export default NutricionHoy;
